import React from 'react';
import { MapPin, Clock, Phone, Mail, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHelmet from '../../components/SEOHelmet';
import Breadcrumbs from '../../components/Breadcrumbs';
import InternalLinks from '../../components/InternalLinks';
import { companyInfo } from '../../mockData';

const PalmDesertPage = () => {
  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh' }}>
      <SEOHelmet 
        title="Process Server Palm Desert CA | Legal Document Services | Legal Document Specialist"
        description="Palm Desert process server specializing in the shopping and business district. Expert legal document service for El Paseo, Desert Willow, and Gardens on El Paseo areas. Licensed process servers with local court knowledge."
        keywords="process server Palm Desert CA, legal document services Palm Desert, court filing Palm Desert, skip tracing Palm Desert, Riverside County process server"
        canonical="https://legaldocspecialist.com/palm-desert-process-server"
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              Process Server Palm Desert, California
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional legal document services in Palm Desert, CA. Registered and bonded process servers providing reliable service throughout Riverside County.
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Now
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Coverage */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Palm Desert Legal Document Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Serving Palm Desert and surrounding Coachella Valley communities with professional legal support services.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Services Column */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <CheckCircle2 size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Services in Palm Desert
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Process Serving',
                  'Court Filing Services',
                  'County Recording',
                  'Document Retrieval',
                  'Skip Tracing & Locate Services'
                ].map((service, index) => (
                  <li key={service} style={{ 
                    padding: '0.75rem 0',
                    borderBottom: index < 4 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <CheckCircle2 size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)' }}>{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Local Court Information */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <MapPin size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Local Court Coverage
              </h3>
              <div style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Palm Springs Courthouse</strong><br />
                  Riverside County Superior Court - Palm Springs Branch<br />
                  3255 E Tahquitz Canyon Way<br />
                  Palm Springs, CA 92262 (10 minutes from Palm Desert)
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Indio Courthouse (Larson Justice Center)</strong><br />
                  46-200 Oasis Street<br />
                  Indio, CA 92201 (15 minutes from Palm Desert)
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  Serving Palm Desert with both local Coachella Valley courthouses for comprehensive legal support and same-day filing services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us for Palm Desert */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Why Choose Legal Document Specialist for Palm Desert?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Local expertise with statewide reach - serving Palm Desert and the entire Coachella Valley.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
            {[
              {
                icon: <MapPin size={32} />,
                title: 'Local Knowledge',
                description: 'Familiar with Palm Desert, Riverside County courts, and local filing procedures.'
              },
              {
                icon: <Clock size={32} />,
                title: 'Fast Response',
                description: 'Same-day service available. Routine, priority, urgent, and on-demand options.'
              },
              {
                icon: <CheckCircle2 size={32} />,
                title: 'Registered & Bonded',
                description: 'Professional process servers registered and bonded for your protection.'
              }
            ].map((feature) => (
              <div key={feature.title} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{ color: 'var(--brand-primary)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  {feature.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{feature.title}</h3>
                <p className="body-text" style={{ color: 'var(--text-secondary)' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Map */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Palm Desert Service Area Coverage
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Comprehensive coverage throughout the Coachella Valley and Riverside County.
            </p>
          </div>

          <div style={{
            backgroundColor: 'var(--bg-page)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
              Cities We Serve from Palm Desert Base
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                'Palm Desert', 'Palm Springs', 'Cathedral City', 'Desert Hot Springs',
                'Rancho Mirage', 'La Quinta', 'Indian Wells', 'Indio',
                'Coachella', 'Riverside', 'Moreno Valley', 'Beaumont'
              ].map((city) => (
              <div key={city} style={{
                  padding: '0.75rem',
                  backgroundColor: 'var(--bg-card)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-light)'
                }}>
                  <span style={{ color: 'var(--text-primary)' }}>{city}</span>
                </div>
              ))}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Plus all other Riverside County communities and statewide service for California clients.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Contact Us for Palm Desert Legal Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Ready to place an order or need a quote? Contact Legal Document Specialist today.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {/* Contact Info */}
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>Get in Touch</h3>
              
              <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Phone size={20} color="var(--brand-primary)" style={{ marginRight: '1rem' }} />
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.phone}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Monday-Friday 9AM-5PM</div>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Mail size={20} color="var(--brand-primary)" style={{ marginRight: '1rem' }} />
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.email}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Email us anytime</div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MapPin size={20} color="var(--brand-primary)" style={{ marginRight: '1rem' }} />
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Legal Document Specialist</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{companyInfo.address}</div>
                </div>
              </div>
            </div>

            {/* Quick Action */}
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              textAlign: 'center'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>Quick Service Request</h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                Need immediate assistance in Palm Desert? Choose your service level:
              </p>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <Link to="/place-order" className="btn-primary" style={{ 
                  textDecoration: 'none',
                  display: 'block',
                  marginBottom: '1rem'
                }}>
                  Place Order Now
                </Link>
                <Link to="/quote" className="btn-secondary" style={{ 
                  textDecoration: 'none',
                  display: 'block'
                }}>
                  Request Free Quote
                </Link>
              </div>

              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                Serving Palm Desert with routine, priority, urgent, and on-demand service options.
              </p>
            </div>
          </div>
        </div>
      </section>

      <InternalLinks currentPage="/cities/palm-desert" />
      <Footer />
    </div>
  );
};

export default PalmDesertPage;